import {
  BoxGeometry,
  Color,
  DynamicDrawUsage,
  InstancedMesh,
  Matrix4,
  MeshStandardMaterial,
  Quaternion,
  Vector3,
  type Scene,
} from 'three';
import type { City, SkyLink } from '@/world/city';
import { PALETTE, AI } from '@/config';
import { clamp01 } from '@/utils/math';

/**
 * Renders the aerial layer the city grows for athletic players: skybridges between
 * rooftops and floating platforms. Bridges extend outward from their start rooftop
 * (growth scales their length), so a bridge visibly *reaches across* the gap rather
 * than blinking into existence — reinforcing the "the city is building this for you"
 * reading of the parkour/verticality response.
 *
 * These double as real traversal geometry: the player controller queries the city's
 * links for surfaces to stand on, so the aerial routes are walkable, not decorative.
 */
const _m = new Matrix4();
const _pos = new Vector3();
const _quat = new Quaternion();
const _scale = new Vector3();
const _dir = new Vector3();
const _from = new Vector3();
const _to = new Vector3();
const _xAxis = new Vector3(1, 0, 0);

export class SkyLinkField {
  readonly mesh: InstancedMesh;
  private freeSlots: number[] = [];
  /** Finale memory: the fraction (0..1) by which the aerial layer has retracted. */
  private targetMemory = 0;
  private appliedMemory = 0;

  constructor(scene: Scene, capacity = 512) {
    const geo = new BoxGeometry(1, 1, 1);
    geo.translate(0.5, 0, 0); // anchor at the start end; grows along +X.

    const mat = new MeshStandardMaterial({
      color: new Color(PALETTE.building).multiplyScalar(0.96),
      emissive: new Color(PALETTE.glow),
      emissiveIntensity: 0.05,
      roughness: 0.8,
      metalness: 0.0,
    });

    this.mesh = new InstancedMesh(geo, mat, capacity);
    this.mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.mesh.frustumCulled = false;
    this.mesh.count = capacity;

    for (let i = capacity - 1; i >= 0; i--) {
      _m.makeScale(0, 0, 0);
      this.mesh.setMatrixAt(i, _m);
      this.freeSlots.push(i);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    scene.add(this.mesh);
  }

  private writeMatrix(link: SkyLink): void {
    if (link.slot < 0) return;
    // Render-side retraction: memory 0 leaves the normal transform untouched; as
    // memory rises toward 1 the visual shrinks toward its anchor, so bridges retract
    // back into their start rooftop and platforms shrink to a point. No model value
    // is touched, so memory returning to 0 restores the aerial layer exactly.
    const mem = clamp01(1 - this.appliedMemory);
    _from.set(link.ax, link.ay, link.az);
    _to.set(link.bx, link.by, link.bz);
    const full = _dir.subVectors(_to, _from).length();

    if (link.kind === 'platform') {
      // Platform: a flat square that scales up in place (uniformly toward its centre).
      const s = link.width * link.growth * mem;
      _pos.copy(_from);
      _quat.identity();
      _scale.set(Math.max(0.0001, s), Math.max(0.0001, 0.35 * mem), Math.max(0.0001, s));
      // Platform geometry is anchored at +X start; recentre it.
      _m.compose(_pos, _quat, _scale);
      _m.multiply(new Matrix4().makeTranslation(-0.5, 0, 0));
    } else {
      // Bridge: extend along the direction from A to B by growth, retracted by memory.
      const len = Math.max(0.0001, full * clamp01(link.growth) * mem);
      _dir.normalize();
      _quat.setFromUnitVectors(_xAxis, _dir);
      _pos.copy(_from);
      _scale.set(len, 0.3, link.width);
      _m.compose(_pos, _quat, _scale);
    }
    this.mesh.setMatrixAt(link.slot, _m);
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  /**
   * Finale hook: retract (m→1) or re-extend (m→0) the whole aerial layer. Cheap —
   * a full matrix rewrite happens only on frames where the value actually moves.
   */
  setMemory(m: number): void {
    this.targetMemory = clamp01(m);
  }

  update(city: City, dt: number): void {
    const rate = dt / (AI.growthDuration * 0.8);
    for (const link of city.links) {
      if (link.slot < 0) {
        const slot = this.freeSlots.pop();
        if (slot === undefined) continue;
        link.slot = slot;
      }
      if (link.growth < 1) {
        link.growth = clamp01(link.growth + rate);
        this.writeMatrix(link);
      }
    }

    // When the finale memory value moves meaningfully, rewrite every live link so
    // the retraction is applied uniformly across the aerial layer.
    if (Math.abs(this.targetMemory - this.appliedMemory) > 0.003) {
      this.appliedMemory = this.targetMemory;
      for (const link of city.links) this.writeMatrix(link);
    }
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as MeshStandardMaterial).dispose();
    this.mesh.removeFromParent();
  }
}
